import { GridColumnState } from '@progress/kendo-react-grid';
import moment from 'moment/moment';
import { LogicOperator } from '../study-data-filter/advanced-select/advanced-select.interface';
import {
    DataType,
    FilterOperatorItem,
    FilterOperators,
} from '@components-clean/core/grid/grid-custom-filter/grid-custom-filter.interface';
import {
    GridColumnProps,
    ORDER_COLUMN_ID,
    REORDER_COLUMN_ID,
    ROW_MENU_ACTION_COLUMN_ID,
} from '@components-clean/core/grid/grid.interface';
import { DragHandleCell } from '@components-clean/core/grid-fe/custom-cells';

export const GRID_HEADER_HEIGHT = 32;
export const SELECTED_FIELD = 'selected';
export const ADJUST_GRID_CONTENT_PADDING = 11;

export const reorderColumn: GridColumnProps = {
    title: 'Order',
    width: 50,
    cells: { data: DragHandleCell },
    id: REORDER_COLUMN_ID,
    showByDefault: false,
    reorderable: false,
};

export const orderColumn: GridColumnProps = {
    title: 'Order',
    field: 'displayIndex',
    lockedByDefault: true,
    show: true,
    showByDefault: true,
    children: undefined,
    width: 75,
    id: ORDER_COLUMN_ID,
    reorderable: false,
};

export const rowMenuActionColumn: GridColumnProps = {
    show: true,
    showByDefault: true,
    reorderable: false,
    resizable: false,
    filterable: false,
    sortable: false,
    id: ROW_MENU_ACTION_COLUMN_ID,
};

export const getDataType = (filterType: GridColumnProps['filter'] | 'float') => {
    switch (filterType) {
        case 'text':
            return DataType.String;
        case 'date':
            return DataType.Date;
        case 'numeric':
            return DataType.Int;
        case 'float':
            return DataType.Decimal;
        case 'boolean':
            return DataType.Bool;
        default:
            return DataType.String;
    }
};

export const updateColumnsChildrenFields = (
    columns: GridColumnProps[] | undefined,
    savedColumnsMap?: Map<string, GridColumnState>,
): GridColumnProps[] | undefined => {
    if (!columns) {
        return undefined;
    }
    return columns.map((column, index) => {
        const savedColumn = savedColumnsMap?.get(column.id ?? '');
        return {
            ...column,
            locked: savedColumn?.locked ?? column.locked ?? column.lockedByDefault ?? false,
            lockedByDefault: column.lockedByDefault ?? false,
            show: savedColumn != null ? !savedColumn.hidden : (column.showByDefault ?? true),
            children: updateColumnsChildrenFields(column.children, savedColumnsMap),
            orderIndex: column.orderIndex ?? index + 1,
        };
    });
};

// If there are issues with Column Widths and Grid size (i.e. empty grid space not used by columns)
// then this could help. It calculates the minWidths based on the child columns, as sometimes the parent
// minWidth does not accurately reflect the sum of child min widths.However, using this right now affects
// all grids, and we don't have time to test all grids. Maybe as part of the next tech debt.
// const childrenMinWidth = (col: GridColumnProps) => {
//     const width = col.minWidth ?? 0;
//     let childWidth = 0;
//     if (col.children && col.children.length) {
//         childWidth = col.children
//             .filter((c) => c.show)
//             .reduce((w, childCol) => {
//                 return w + childrenMinWidth(childCol);
//             }, 0);
//     }
//     return childWidth || width;
// };

export const getMinGridWidth = (columns: GridColumnProps[]) => {
    let minWidth = 0;
    const resizableCols = columns.filter((col) => col.show && !col.customColumn);
    const customCols = columns.filter((col) => col.show && col.customColumn);
    let customColsWidth = 0;

    resizableCols.forEach((item) => {
        // minWidth += childrenMinWidth(item);
        if (item.minWidth !== undefined) {
            minWidth += item.minWidth;
        } else if (item.children) {
            const childrenMinWidthTotal = item.children
                ?.filter((col) => col.show)
                ?.reduce((n, { minWidth: childMinWidth }) => n + (childMinWidth ?? 0), 0);

            if (childrenMinWidthTotal) minWidth += childrenMinWidthTotal;
        }
    });

    if (customCols.length) {
        customCols.forEach((col) => {
            customColsWidth += col.width ? Number(col.width) : Number(col.minWidth);
        });
    }

    minWidth += customColsWidth;

    return minWidth;
};

export const applyOperatorLogic = (
    current: string | boolean | null | undefined,
    value: string | Date | boolean | null | undefined,
    operator: FilterOperatorItem,
    dataType?: DataType,
) => {
    if (typeof value === 'string') {
        current = String(current);
        switch (operator.value) {
            case FilterOperators.Contains:
                return current?.toLowerCase()?.includes(value.toLowerCase());
            case FilterOperators.DoesNotContain:
                return !current?.toLowerCase()?.includes(value.toLowerCase());
            case FilterOperators.EndsWith:
                return current?.toLowerCase()?.endsWith(value.toLowerCase());
            case FilterOperators.StartsWith:
                return current?.toLowerCase()?.startsWith(value.toLowerCase());
            case FilterOperators.Equal:
                return current?.toLowerCase() === value.toLowerCase();
            case FilterOperators.NotEqual:
                return current?.toLowerCase() !== value.toLowerCase();
            case FilterOperators.LessThan:
                return +current < +value;
            case FilterOperators.LessThanEqualTo:
                return +current <= +value;
            case FilterOperators.GreaterThan:
                return +current > +value;
            case FilterOperators.GreaterThanEqualTo:
                return +current >= +value;
            default:
                return null;
        }
    } else if (moment.isMoment(value)) {
        current = String(current).replace('Z', '');
        switch (operator.value) {
            case FilterOperators.Equal:
                return moment(current).startOf('day').isSame(value);
            case FilterOperators.NotEqual:
                return !moment(current).startOf('day').isSame(value);
            case FilterOperators.LessThan:
                return moment(current) < value;
            case FilterOperators.LessThanEqualTo:
                return moment(current) <= moment(value).endOf('day');
            case FilterOperators.GreaterThan:
                return moment(current) > moment(value).endOf('day');
            case FilterOperators.GreaterThanEqualTo:
                return moment(current) >= value;
            default:
                return null;
        }
    } else if (typeof value === 'boolean') {
        switch (operator.value) {
            case FilterOperators.Equal:
                return current === value;
            case FilterOperators.NotEqual:
                return current !== value;
            default:
                return null;
        }
    } else if (value == null) {
        switch (operator.value) {
            case FilterOperators.IsNull:
                return dataType === DataType.String ? current == null || current === '' : current == null;
            case FilterOperators.IsNotNull:
                return dataType === DataType.String ? current != null && current !== '' : current != null;
            default:
                return null;
        }
    }
};

export const isNumberDecimal = (value: string | boolean | null | undefined) => {
    if (value == null || typeof value === 'boolean') return false;
    const float = Number.parseFloat(value);
    if (isNaN(float)) return false;

    return float.toString().split('.').length > 1;
};

export const applyDecimalOperator = (op1: FilterOperatorItem, current: string, value: number) => {
    // `toString` on a number will always result in a '.' for the decimal point
    // The alternative would be to replace any `,` with `.` in the search string
    // eslint-disable-next-line max-len
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toString#:~:text=If%20the%20number%20is%20not,to%20separate%20the%20decimal%20places.
    const split = value.toString().split('.');
    const scale = split.length === 2 ? split[1].length : 0;
    const precision = 10 ** (scale * -1);
    const halfPrecision = precision / 2;

    // This logic matches the BE API code for decimal filtering. Look in SQLQueryBuilder.cs, GetComparisonOperatorDecimal method
    const currentNumber = Number.parseFloat(current);
    switch (op1.value) {
        case FilterOperators.Equal: {
            const upperLimit = value + halfPrecision * 2;
            // e.g. x == 12.3 becomes x >= 12.3 && x < 12.4
            return (
                applyOperatorLogic(current, value.toString(), {
                    ...op1,
                    value: FilterOperators.GreaterThanEqualTo,
                }) &&
                applyOperatorLogic(current, upperLimit.toString(), {
                    ...op1,
                    value: FilterOperators.LessThan,
                })
            );
        }
        case FilterOperators.NotEqual: {
            // e.g. x != 12.3 becomes x < 12.3 && x >= 12.4
            const upperLimit = value + halfPrecision * 2;
            return (
                applyOperatorLogic(current, value.toString(), {
                    ...op1,
                    value: FilterOperators.LessThan,
                }) ||
                applyOperatorLogic(current, upperLimit.toString(), {
                    ...op1,
                    value: FilterOperators.GreaterThanEqualTo,
                })
            );
        }

        case FilterOperators.GreaterThan:
        case FilterOperators.LessThanEqualTo:
            // e.x = 5.001 and g x > 5, becomes x - 5 > 0.5. Treats 5.001 as 5 (which is what's displayed) Therefore wont match
            return applyOperatorLogic((currentNumber - value).toString(), halfPrecision.toString(), op1);
        case FilterOperators.GreaterThanEqualTo:
        case FilterOperators.LessThan:
            return applyOperatorLogic((currentNumber - value).toString(), (halfPrecision * -1).toString(), op1);
        default: {
            return applyOperatorLogic(current, value.toString(), op1);
        }
    }
};

const applyOperator = (
    current: string | boolean | null | undefined,
    value: { search: string | Date | boolean | null | undefined },
    operator: FilterOperatorItem,
    filterType: GridColumnProps['filter'],
    filterKey?: string,
) => {
    if (value === undefined) return true;
    if (current && filterKey && typeof current === 'object') current = current[filterKey];
    if (filterType === 'numeric' && isNumberDecimal(current) && value != null) {
        const float = Number.parseFloat(value.search!.toString());
        if (!isNaN(float)) return applyDecimalOperator(operator, current!.toString(), float);
    }

    return applyOperatorLogic(current, value.search, operator);
};

export const calcOperator = (
    op1: FilterOperatorItem,
    filterType: GridColumnProps['filter'],
    filterKey?: string,
    comparisonFilterKey?: string,
) => {
    return (current: string, search: { search: string | Date | boolean | null | undefined }) => {
        if (!comparisonFilterKey) return applyOperator(current, search, op1, filterType, filterKey);
        return (
            applyOperator(current, search, op1, filterType, filterKey) ||
            applyOperator(current, search, op1, filterType, comparisonFilterKey)
        );
    };
};

const applyOperatorAdvanced = (
    current: string | boolean | null | undefined,
    value: string | Date | boolean | null | undefined,
    operator: FilterOperatorItem,
) => {
    if (value === undefined) return true;
    return applyOperatorLogic(current, value, operator, DataType.String);
};

export const calcOperatorAdvanced = (op1: FilterOperatorItem, op2: FilterOperatorItem, value: LogicOperator) => {
    return (current: string, { value1, value2 }: { value1: string; value2: string }) =>
        value === LogicOperator.AND
            ? applyOperatorAdvanced(current, value1, op1) && applyOperatorAdvanced(current, value2, op2)
            : (value1 !== undefined && applyOperatorAdvanced(current, value1, op1)) ||
              (value2 !== undefined && applyOperatorAdvanced(current, value2, op2));
};

export const fieldObjectCompare = (
    sortField: string,
    filterKey: string,
    a: { [key: string]: { [key: string]: string | number | null | undefined } },
    b: { [key: string]: { [key: string]: string | number | null | undefined } },
    dir: string,
): number => {
    let aField: string | number | null | undefined;
    let bField: string | number | null | undefined;

    const sortFieldParts = sortField.split('.');
    let aTree = a;
    sortFieldParts.forEach((part) => {
        if (part in aTree && typeof aTree[part] === 'object') {
            // @ts-ignore
            aTree = aTree[part];
        }
    });

    if (aTree && filterKey in aTree) {
        aField = aTree[filterKey] as unknown as string | number;
    }

    let bTree = b;
    sortFieldParts.forEach((part) => {
        if (part in bTree && typeof bTree[part] === 'object') {
            // @ts-ignore
            bTree = bTree[part];
        }
    });

    if (bTree && filterKey in bTree) {
        bField = bTree[filterKey] as unknown as string | number;
    }

    // This ensures any nulls are sorted together
    if (aField == null && bField == null) return 0;
    if (aField == null) return dir === 'asc' ? -1 : 1;
    if (bField == null) return dir === 'asc' ? 1 : -1;

    return dir === 'asc'
        ? aField === bField
            ? 0
            : aField > bField
              ? 1
              : -1
        : aField === bField
          ? 0
          : aField < bField
            ? 1
            : -1;
};
